import { useEffect, useState } from "react";
import { hasOwn } from "@core/utility/utility";
import IntelLayout from "@components/layout/layout";
import Header from "./components/Header";
import ProfileDetails from "./components/ProfileDetails";
import styles from "./CompanyProfile.module.scss";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { useLocation, useParams } from "react-router-dom";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { profileTypeEnum } from "@components/templates/Profile/ProfileHeader/profile-enum";
import { getEventName, getUserActivePlan } from "@helpers/profileHelper";
import { companyEvents } from "./utils/enum.js";
import { useSelector } from "react-redux";
import NudgePopup from "@shared/country-components/nudgePopup";
import axios from "axios";
import { globalAPIDictionary } from "@core/global/global-country-dictionary";
import { nudgePopupDataCompanyProfile } from "@core/dictionary/intel-nudge";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import GetInTouchLimit from "@components/templates/GetInTouchLimit/GetInTouchLimit";
import { CLOUDINARY_INTEL_BASE_URL, ERROR_MSG } from "@utils/enum";
import { toast } from "react-toastify";
import { getTitleCase } from "@utils/utils";
import { getApiUrl } from "@utils/baseUrl";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import Loader from "@components/base/loader/Loader";

const CompanyProfile = ({ isSalesForce = false }) => {
  const [showNotification, setShowNotification] = useState(false);
  const [panelList, setPanelList] = useState([]);
  const { search: searchUrlParams, pathname } = useLocation();
  const searchParams = new URLSearchParams(searchUrlParams);
  const { companyname } = useParams();
  const country = searchParams.get("country");
  const type = searchParams.get("type");
  const dataSource = searchParams.get("dataSource") || country + "-" + type;
  const profileLocked = searchParams.get("profileLocked");

  const [isProfileViewExhaust, setIsProfileViewExhaust] = useState("pending");
  const creditDetails = useSelector((state) => state.userDetails.creditDetails);
  const STARTERPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("starter"))?.navigate === "search";
  let nudgeCacheStates = JSON.parse(localStorage.getItem("nudgeCache")) || {};

  //nudge code
  let nudgePopupTimer = 2000; //ms
  const [currentNudgePopupKey, setCurrentNudgePopupKey] = useState(null);
  const [nudgePopupStateObj, setNudgePopupStateObj] = useState({ ...nudgeCacheStates });
  const [showNudgePopup, setShowNudgePopup] = useState(false);
  const [showGetInTouch, setShowGetInTouch] = useState(false);
  const [getInTouchLoading, setGetInTouchLoading] = useState(false);

  useEffect(() => {
    trademoTrackingEvents(getEventName(profileTypeEnum.company, companyEvents.landing), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  }, [dataSource, pathname]);

  const handleNudgePopup = (key, callingFrom, event, isPoped) => {
    if (!key) return;
    if (callingFrom && callingFrom === "event") {
      if (nudgePopupTimer) {
        clearTimeout(nudgePopupTimer);
      }
      nudgePopupTimer = setTimeout(() => {
        if (key === "due_diligence_reports" && currentNudgePopupKey === null && nudgePopupStateObj && hasOwn(nudgePopupStateObj, key) && nudgePopupStateObj[key].shouldPop === true && showNudgePopup === false) {
          setCurrentNudgePopupKey(key);
          setShowNudgePopup(true);
        }
      }, nudgePopupStateObj?.[key]?.timer || 4000);
    } else if (callingFrom && callingFrom === "popup") {
      axios.get(globalAPIDictionary.featureNudgeUpdate + `?is_popped=${isPoped !== undefined ? isPoped : true}&feature_name=${key}`);
      setShowNudgePopup(false);
      let tempObj = JSON.parse(JSON.stringify(nudgePopupStateObj));
      if (nudgePopupStateObj && hasOwn(nudgePopupStateObj, key)) {
        tempObj[key]["shouldPop"] = false;
      }
      localStorage.setItem("nudgeCache", JSON.stringify(tempObj));
      setNudgePopupStateObj({ ...tempObj });
      if (event && event === "close") {
        // checking if user wants to close the popup
        if (key === "due_diligence_reports") {
          trademoTrackingEvents(trackingEventDictionary.dueDiligenceNudgeClose, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
        }
      }
    }
  };

  if (isSalesForce) {
    return (
      <div className={`${styles.companyProfileContainer} tw-px-5`}>
        <Header profileType="company" isSalesForce={isSalesForce} setIsProfileViewExhaust={setIsProfileViewExhaust} isProfileViewExhaust={isProfileViewExhaust} />
        <ProfileDetails dataset={`${country}_${type}`} isSalesForce={isSalesForce} handleNudgePopup={handleNudgePopup} panelList={panelList} setPanelList={setPanelList} />
      </div>
    );
  }

  const handleGetInTouchSubmit = async () => {
    setGetInTouchLoading(true);

    try {
      const { user_active_plan } = creditDetails.data?.[0] ?? "";
      const planId = user_active_plan;
      const companyName = companyname?.replace(/[-_]+/g, " ");
      const companyNameParam = getTitleCase(companyName);
      const params = {
        planId,
        countries: `${country?.toLowerCase()}_${type?.toLowerCase()}`,
        key: "company-profile",
        company_name: companyNameParam,
      };
      const { data } = await axios.get(getApiUrl("notifySales"), { params });
      if (data?.status?.toLowerCase() === "success") {
        toast.success(data.message);
      } else {
        toast.error(data.message || ERROR_MSG);
      }
    } catch {
      toast.success(ERROR_MSG);
    } finally {
      setGetInTouchLoading(false);
    }
  };

  return (
    <>
      <IntelLayout
        setNotification={(val) => {
          setShowNotification(val);
        }}
        showNotification={showNotification}
        userDetails={() => {
          return;
        }}
        userPlanDetails={() => {
          return;
        }}
        handleGetInTouchOpen={() => {
          setShowGetInTouch(true);
          return;
        }}
        type={"companyProfile"}>
        <div className={`${styles.companyProfileContainer}`}>
          <Header profileType="company" setIsProfileViewExhaust={setIsProfileViewExhaust} isProfileViewExhaust={isProfileViewExhaust} isStarterPlan={STARTERPLAN} />

          {isProfileViewExhaust !== "pending" ? (
            <>
              {(isProfileViewExhaust || profileLocked) && <div className="tw-w-full tw-bg-gray-300 tw-border tw-my-2"></div>}
              {isProfileViewExhaust || profileLocked ? (
                <div className="tw-h-[calc(100vh-200px)] tw-min-h-96 tw-flex tw-items-center tw-justify-center">
                  <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden tw-max-w-[550px]">
                    {isProfileViewExhaust && <div className="tw-text-base tw-font-medium tw-pl-4 tw-py-2">You have reached your daily profile view limit.</div>}
                    <div className=" tw-flex tw-items-center tw-justify-center tw-border-b tw-py-8 tw-border-[#e9ecef] tw-bg-[url('https://res.cloudinary.com/drzta9shk/image/upload/v1684821201/intel/icons/orange-gradient-bg_x0rmp0.svg')] tw-bg-cover tw-bg-center">
                      <div className="tw-flex tw-items-center tw-border-2 tw-border-white tw-rounded-xl tw-py-4 tw-px-12 tw-bg-white tw-bg-opacity-10 tw-backdrop-blur-sm">
                        <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1684478309/intel/icons/Lightning-alt_t9mkjb.svg`} alt="Lightning" className="tw-h-16" />
                      </div>
                    </div>
                    <div className="tw-flex tw-flex-col tw-py-6 tw-px-6 tw-text-center tw-text-gray-900">
                      {profileLocked && <div className="tw-font-medium tw-text-sm tw-text-gray-600">You do not have access to the trade data for this company as a part of your subscription. Please reach out to our customer success to get access</div>}
                      {isProfileViewExhaust && (
                        <>
                          <div className="tw-font-semibold tw-text-sm">Connect with CSM to Upgrade your Plan and View Unlimited Profiles</div>
                          <div className="tw-font-medium tw-text-sm tw-mt-1">Or to view new profiles visit the platform after 24hrs</div>
                        </>
                      )}
                      <div className="tw-flex tw-gap-3 tw-justify-center tw-mt-6">
                        <button className="tw-bg-primary-purple-500 hover:tw-bg-primary-purple-600 tw-text-white tw-outline-none tw-border-none tw-rounded-lg tw-font-medium tw-px-6 tw-py-2 tw-transition-colors" onClick={handleGetInTouchSubmit} disabled={getInTouchLoading}>
                          Get In Touch
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <ProfileDetails isSalesForce={isSalesForce} panelList={panelList} setPanelList={setPanelList} handleNudgePopup={handleNudgePopup} isProfileViewExhaust={isProfileViewExhaust} isStarterPlan={STARTERPLAN} />
              )}
            </>
          ) : (
            <div className="tw-pl-3">
              <LoadingSkeleton lines={8} lineHeight="35px" width="100%" shape="rectangle" />
            </div>
          )}
        </div>
        {showGetInTouch && <GetInTouchLimit showGetInTouchPopup={showGetInTouch} handleGetInTouchClose={() => setShowGetInTouch(false)} profileType={profileTypeEnum.company} notifyCountry={`${country}_${type}`} blockAccessType={"connect"} />}
      </IntelLayout>
      {showNudgePopup && <NudgePopup handleNudgePopup={(event) => handleNudgePopup(currentNudgePopupKey, "popup", event)} nudgePopupStateObj={nudgePopupDataCompanyProfile} currentNudgePopupKey={currentNudgePopupKey} />}
      {getInTouchLoading && <Loader />}
    </>
  );
};

export default CompanyProfile;
